import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, ChevronLeft, ChevronRight, Save, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/authContext";
import { useLocation } from "@/lib/locationContext";
import { useAppContext } from "@/lib/onboardingContext";
import { supabase } from "@/integrations/supabase/client";
import { getCurrencySymbol, formatPercent } from "@/lib/currency";
import { MetricNumber } from "@/components/ui/metric-number";
import { useBenchmarks } from "@/lib/useBenchmarks";
import { toast } from "sonner";

const BRAND_GREEN = "#2d8653";
const BRAND_ORANGE = "#e76f00";
const BRAND_DARK = "#1a1a1a";
const BRAND_RED = "#c0392b";

type PnL = {
  id?: string;
  month_label: string;
  food_revenue: number;
  beverage_revenue: number;
  food_cost: number;
  beverage_cost: number;
  foh_labor: number;
  boh_labor: number;
  management_cost: number;
  rent: number;
  utilities: number;
  marketing: number;
  repairs_maintenance: number;
  insurance: number;
  other_expenses: number;
  notes?: string | null;
};

const EMPTY: PnL = {
  month_label: "",
  food_revenue: 0,
  beverage_revenue: 0,
  food_cost: 0,
  beverage_cost: 0,
  foh_labor: 0,
  boh_labor: 0,
  management_cost: 0,
  rent: 0,
  utilities: 0,
  marketing: 0,
  repairs_maintenance: 0,
  insurance: 0,
  other_expenses: 0,
  notes: "",
};

/** Returns "YYYY-MM" for a Date. */
function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(key: string): string {
  const [y, m] = key.split("-").map(Number);
  if (!y || !m) return key;
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
}

function shiftMonth(key: string, delta: number): string {
  const [y, m] = key.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return monthKey(d);
}

function pct(part: number, whole: number): number {
  if (!whole) return 0;
  return Math.round((part / whole) * 1000) / 10;
}

/** Traffic-light tone for a percentage vs target.
 *  Lower is better (cost % of revenue). */
function tonePct(value: number, target: number) {
  if (value <= target) return BRAND_GREEN;
  if (value <= target + 5) return BRAND_ORANGE;
  return BRAND_RED;
}

/** Traffic-light tone for net profit margin (higher is better). */
function toneMargin(margin: number) {
  if (margin >= 10) return BRAND_GREEN;
  if (margin >= 3) return BRAND_ORANGE;
  return BRAND_RED;
}

export default function PnLStatement() {
  const { user } = useAuth();
  const { activeLocation } = useLocation();
  const { onboarding } = useAppContext();
  const { benchmarks } = useBenchmarks();
  const cs = getCurrencySymbol(onboarding.country || onboarding.city || "");

  const [currentMonth, setCurrentMonth] = useState(monthKey(new Date()));
  const [pnl, setPnl] = useState<PnL>({ ...EMPTY, month_label: monthKey(new Date()) });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Load current month record (or blank).
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      let q = (supabase as any)
        .from("pnl_statements")
        .select("*")
        .eq("user_id", user.id)
        .eq("month_label", currentMonth)
        .limit(1);
      if (activeLocation?.id) q = q.eq("location_id", activeLocation.id);
      else q = q.is("location_id", null);
      const { data } = await q;
      if (cancelled) return;
      const row = data?.[0];
      if (row) {
        setPnl({
          id: row.id,
          month_label: row.month_label,
          food_revenue: Number(row.food_revenue) || 0,
          beverage_revenue: Number(row.beverage_revenue) || 0,
          food_cost: Number(row.food_cost) || 0,
          beverage_cost: Number(row.beverage_cost) || 0,
          foh_labor: Number(row.foh_labor) || 0,
          boh_labor: Number(row.boh_labor) || 0,
          management_cost: Number(row.management_cost) || 0,
          rent: Number(row.rent) || 0,
          utilities: Number(row.utilities) || 0,
          marketing: Number(row.marketing) || 0,
          repairs_maintenance: Number(row.repairs_maintenance) || 0,
          insurance: Number(row.insurance) || 0,
          other_expenses: Number(row.other_expenses) || 0,
          notes: row.notes ?? "",
        });
      } else {
        setPnl({ ...EMPTY, month_label: currentMonth });
      }
      setDirty(false);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user, currentMonth, activeLocation?.id]);

  const totals = useMemo(() => {
    const totalRevenue = pnl.food_revenue + pnl.beverage_revenue;
    const totalCogs = pnl.food_cost + pnl.beverage_cost;
    const totalLabor = pnl.foh_labor + pnl.boh_labor + pnl.management_cost;
    const totalOpex =
      pnl.rent + pnl.utilities + pnl.marketing +
      pnl.repairs_maintenance + pnl.insurance + pnl.other_expenses;
    const grossProfit = totalRevenue - totalCogs;
    const operatingProfit = grossProfit - totalLabor - totalOpex;
    const netMargin = pct(operatingProfit, totalRevenue);
    return {
      totalRevenue, totalCogs, totalLabor, totalOpex,
      grossProfit, operatingProfit, netMargin,
      foodCostPct: pct(pnl.food_cost, pnl.food_revenue || totalRevenue),
      bevCostPct: pct(pnl.beverage_cost, pnl.beverage_revenue || totalRevenue),
      cogsPct: pct(totalCogs, totalRevenue),
      laborPct: pct(totalLabor, totalRevenue),
      rentPct: pct(pnl.rent, totalRevenue),
    };
  }, [pnl]);

  const setField = (key: keyof PnL, value: number | string) => {
    setPnl((p) => ({ ...p, [key]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    const payload: any = {
      user_id: user.id,
      location_id: activeLocation?.id ?? null,
      month_label: pnl.month_label,
      food_revenue: pnl.food_revenue,
      beverage_revenue: pnl.beverage_revenue,
      food_cost: pnl.food_cost,
      beverage_cost: pnl.beverage_cost,
      foh_labor: pnl.foh_labor,
      boh_labor: pnl.boh_labor,
      management_cost: pnl.management_cost,
      rent: pnl.rent,
      utilities: pnl.utilities,
      marketing: pnl.marketing,
      repairs_maintenance: pnl.repairs_maintenance,
      insurance: pnl.insurance,
      other_expenses: pnl.other_expenses,
      notes: pnl.notes ?? null,
    };
    let result;
    if (pnl.id) {
      result = await (supabase as any)
        .from("pnl_statements")
        .update(payload)
        .eq("id", pnl.id)
        .select("id")
        .maybeSingle();
    } else {
      result = await (supabase as any)
        .from("pnl_statements")
        .insert(payload)
        .select("id")
        .maybeSingle();
    }
    setSaving(false);
    if (result.error) {
      toast.error(result.error.message || "Could not save P&L");
      return;
    }
    if (result.data?.id) setPnl((p) => ({ ...p, id: result.data.id }));
    setDirty(false);
    toast.success(`Saved P&L for ${monthLabel(currentMonth)}`);
  };

  const fmt = (n: number) =>
    `${n < 0 ? "−" : ""}${cs}${Math.abs(Math.round(n)).toLocaleString()}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-border bg-card p-4 sm:p-6 shadow-sm"
    >
      {/* Header */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-5">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${BRAND_GREEN}1a` }}
        >
          <TrendingUp className="w-4 h-4" style={{ color: BRAND_GREEN }} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-heading text-base sm:text-lg font-medium" style={{ color: BRAND_DARK }}>
            Financial Health — Full P&L
          </h2>
          <p className="text-xs text-muted-foreground hidden sm:block">
            Month-by-month profit & loss with traffic-light health
          </p>
        </div>

        {/* Month nav */}
        <div className="flex items-center gap-1 rounded-lg border border-border bg-background order-3 sm:order-none basis-full sm:basis-auto justify-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentMonth((m) => shiftMonth(m, -1))}
            aria-label="Previous month"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="px-2 text-sm font-medium tabular-nums flex-1 sm:flex-none sm:min-w-[8rem] text-center"
                style={{ color: BRAND_DARK }}>
            {monthLabel(currentMonth)}
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentMonth((m) => shiftMonth(m, +1))}
            aria-label="Next month"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving || loading || !dirty}
          className="gap-2"
          style={{ backgroundColor: BRAND_GREEN }}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </Button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          Loading…
        </div>
      ) : (
        <div className="space-y-6">
          {/* REVENUE */}
          <Section title="Revenue">
            <PnLInputRow label="Food revenue" value={pnl.food_revenue} cs={cs}
              onChange={(v) => setField("food_revenue", v)} />
            <PnLInputRow label="Beverage revenue" value={pnl.beverage_revenue} cs={cs}
              onChange={(v) => setField("beverage_revenue", v)} />
            <PnLTotalRow label="Total revenue" value={fmt(totals.totalRevenue)} accent={BRAND_GREEN} />
          </Section>

          {/* COGS */}
          <Section title="Cost of Goods Sold">
            <PnLInputRow
              label="Food cost"
              value={pnl.food_cost}
              cs={cs}
              onChange={(v) => setField("food_cost", v)}
              suffix={`${totals.foodCostPct}%`}
              suffixColor={tonePct(totals.foodCostPct, benchmarks.foodPct)}
            />
            <PnLInputRow
              label="Beverage cost"
              value={pnl.beverage_cost}
              cs={cs}
              onChange={(v) => setField("beverage_cost", v)}
              suffix={`${totals.bevCostPct}%`}
              suffixColor={tonePct(totals.bevCostPct, 22)}
            />
            <PnLTotalRow
              label="Total COGS"
              value={`${fmt(totals.totalCogs)} · ${totals.cogsPct}%`}
              accent={tonePct(totals.cogsPct, benchmarks.foodPct)}
            />
          </Section>

          {/* LABOR */}
          <Section title="Labor">
            <PnLInputRow label="FOH labor" value={pnl.foh_labor} cs={cs}
              onChange={(v) => setField("foh_labor", v)} />
            <PnLInputRow label="BOH labor" value={pnl.boh_labor} cs={cs}
              onChange={(v) => setField("boh_labor", v)} />
            <PnLInputRow label="Management" value={pnl.management_cost} cs={cs}
              onChange={(v) => setField("management_cost", v)} />
            <PnLTotalRow
              label="Total labor"
              value={`${fmt(totals.totalLabor)} · ${totals.laborPct}%`}
              accent={tonePct(totals.laborPct, benchmarks.laborPct)}
            />
          </Section>

          {/* OPEX */}
          <Section title="Operating Expenses">
            <PnLInputRow
              label="Rent"
              value={pnl.rent}
              cs={cs}
              onChange={(v) => setField("rent", v)}
              suffix={`${totals.rentPct}%`}
              suffixColor={tonePct(totals.rentPct, benchmarks.rentPct)}
            />
            <PnLInputRow label="Utilities" value={pnl.utilities} cs={cs}
              onChange={(v) => setField("utilities", v)} />
            <PnLInputRow label="Marketing" value={pnl.marketing} cs={cs}
              onChange={(v) => setField("marketing", v)} />
            <PnLInputRow label="Repairs & maintenance" value={pnl.repairs_maintenance} cs={cs}
              onChange={(v) => setField("repairs_maintenance", v)} />
            <PnLInputRow label="Insurance" value={pnl.insurance} cs={cs}
              onChange={(v) => setField("insurance", v)} />
            <PnLInputRow label="Other expenses" value={pnl.other_expenses} cs={cs}
              onChange={(v) => setField("other_expenses", v)} />
            <PnLTotalRow label="Total operating expenses" value={fmt(totals.totalOpex)} accent={BRAND_DARK} />
          </Section>

          {/* BOTTOM LINE */}
          <div className="rounded-xl border-2 p-4 space-y-3"
               style={{ borderColor: toneMargin(totals.netMargin) }}>
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
              Bottom Line
            </div>
            <BottomRow label="Gross profit" value={fmt(totals.grossProfit)}
              color={totals.grossProfit >= 0 ? BRAND_GREEN : BRAND_RED} />
            <BottomRow label="Operating profit" value={fmt(totals.operatingProfit)}
              color={totals.operatingProfit >= 0 ? BRAND_GREEN : BRAND_RED} />
            <div className="border-t border-border pt-3 flex items-center justify-between">
              <span className="font-medium" style={{ color: BRAND_DARK }}>
                Net profit margin
              </span>
              <span style={{ color: toneMargin(totals.netMargin) }} className="min-w-0 max-w-[55%]">
                <MetricNumber
                  value={totals.totalRevenue > 0 ? formatPercent(totals.netMargin) : "—"}
                  className="text-2xl"
                  center={false}
                />
              </span>
            </div>
          </div>

          {dirty && (
            <p className="text-xs italic text-muted-foreground text-right">
              Unsaved changes — click Save to record this month.
            </p>
          )}
        </div>
      )}
    </motion.div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">
        {title}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function PnLInputRow({
  label, value, onChange, cs, suffix, suffixColor,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  cs: string;
  suffix?: string;
  suffixColor?: string;
}) {
  return (
    <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 sm:gap-3">
      <label className="flex-1 min-w-0 text-sm text-muted-foreground basis-full sm:basis-auto">{label}</label>
      <div className="flex items-center gap-2 ml-auto">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
            {cs}
          </span>
          <Input
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            value={Number.isFinite(value) ? value : 0}
            onChange={(e) => onChange(Number(e.target.value) || 0)}
            className="w-36 sm:w-36 h-10 pl-7 text-right tabular-nums"
          />
        </div>
        {suffix && (
          <span
            className="text-xs font-medium tabular-nums w-12 text-right"
            style={{ color: suffixColor ?? BRAND_DARK }}
          >
            {suffix}
          </span>
        )}
        {!suffix && <span className="w-12" />}
      </div>
    </div>
  );
}

function PnLTotalRow({ label, value, accent }: { label: string; value: string; accent: string }) {
  return (
    <div className="flex items-center justify-between border-t border-border pt-2 mt-1">
      <span className="text-sm font-medium" style={{ color: BRAND_DARK }}>{label}</span>
      <span className="text-sm font-medium tabular-nums" style={{ color: accent }}>{value}</span>
    </div>
  );
}

function BottomRow({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-base font-medium tabular-nums" style={{ color }}>{value}</span>
    </div>
  );
}